import React, { useState, useEffect, useRef } from 'react';
import parse from 'html-react-parser';
import type { Message as MessageType } from '../../types/chat.types';
import { sanitizeHtml } from '../../utils/htmlSanitizer';
import { parseMarkdown } from '../../utils/markdownParser';
import { balanceHtmlTags } from '../../utils/streamHtml';
import { ImageLightbox } from '../ImageLightbox';
import { calculateAlbumLayout, type AlbumLayout } from './albumLayout';
import { usePreset, useAssetUrl } from '../../lib/ThemeContext';

interface MessageProps {
  message: MessageType;
  animate?: boolean;
}

const MAX_ALBUM_WIDTH = 300;

/**
 * Message component displaying a single message
 */
export const Message: React.FC<MessageProps> = React.memo(({ message, animate = false }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [imageRatios, setImageRatios] = useState<number[]>([]);
  const [albumLayout, setAlbumLayout] = useState<AlbumLayout | null>(null);
  const loadedCountRef = useRef(0);
  const preset = usePreset();
  const assetUrl = useAssetUrl();
  const isNv = preset === 'nv';
  const isNevi = preset === 'nevi';
  const isPresetStyled = isNv || isNevi;

  // Resolve relative image paths (e.g. /images/file.jpg) to full URLs via assetBaseUrl
  const resolvedImages = message.images?.map((src) => (src.startsWith('/') ? assetUrl(src) : src));
  const resolvedFiles = message.files?.map((src) => (src.startsWith('/') ? assetUrl(src) : src));

  const isBot = message.sender.toLowerCase() === 'bot';
  const timestamp = new Date(message.createdAt).toLocaleTimeString('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  const handleImageClick = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const hasImages = resolvedImages && resolvedImages.length > 0;
  const hasFiles = resolvedFiles && resolvedFiles.length > 0;
  const hasText = !!message.content;
  const imageCount = resolvedImages?.length || 0;
  const isSingleImageNoText = hasImages && imageCount === 1 && !hasText;
  const isMultipleImages = hasImages && imageCount > 1;

  // Load image dimensions for album layout
  useEffect(() => {
    if (!isMultipleImages || !resolvedImages) return;

    let cancelled = false;
    loadedCountRef.current = 0;
    const ratios: number[] = new Array(resolvedImages.length).fill(1);

    resolvedImages.forEach((src, index) => {
      const img = new Image();
      img.onload = () => {
        if (cancelled) return;
        ratios[index] = img.width / img.height;
        loadedCountRef.current++;

        if (loadedCountRef.current === resolvedImages!.length) {
          setImageRatios([...ratios]);
        }
      };
      img.onerror = () => {
        if (cancelled) return;
        loadedCountRef.current++;
        if (loadedCountRef.current === resolvedImages!.length) {
          setImageRatios([...ratios]);
        }
      };
      img.src = src;
    });

    return () => {
      cancelled = true;
    };
  }, [isMultipleImages, resolvedImages]);

  // Calculate album layout when ratios are ready
  useEffect(() => {
    if (imageRatios.length > 1) {
      const layout = calculateAlbumLayout(imageRatios, MAX_ALBUM_WIDTH);
      setAlbumLayout(layout);
    }
  }, [imageRatios]);

  const getFileName = (url: string): string => {
    const parts = url.split('/');
    return parts[parts.length - 1] || 'file';
  };

  const getFileIcon = (url: string): string => {
    const name = getFileName(url).toLowerCase();
    if (name.endsWith('.pdf')) return '\ud83d\udcc4';
    if (name.match(/\.(py|ts|tsx|js|jsx|go|rs|java|rb|php|c|cpp|h)$/)) return '\ud83d\udcbb';
    if (name.match(/\.(json|yaml|yml|xml|toml|ini|cfg|conf|env)$/)) return '\u2699\ufe0f';
    if (name.match(/\.(csv|xls|xlsx)$/)) return '\ud83d\udcca';
    if (name.match(/\.(doc|docx|txt|md|rtf)$/)) return '\ud83d\udcc3';
    return '\ud83d\udcce';
  };

  const renderFiles = () => {
    if (!hasFiles) return null;
    return (
      <div className="wc-flex wc-flex-col wc-gap-1 wc-mt-1">
        {resolvedFiles!.map((fileUrl, index) => (
          <a
            key={index}
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="wc-flex wc-items-center wc-gap-2 wc-px-2 wc-py-1.5 wc-rounded-lg wc-no-underline wc-transition-colors"
            style={{
              background: isBot ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.15)',
              color: 'inherit',
              fontSize: '12px',
            }}
            download
          >
            <span style={{ fontSize: '16px' }}>{getFileIcon(fileUrl)}</span>
            <span className="wc-truncate" style={{ maxWidth: '180px' }}>
              {getFileName(fileUrl)}
            </span>
          </a>
        ))}
      </div>
    );
  };

  // Only user messages animate on appear. Bot messages replace a live-streaming draft,
  // so animating them in would read as a jump over already-visible text.
  const animationClass = animate && !isBot ? 'wc-message-appear-user' : '';

  // NV preset styles — exact Figma values
  const nvUserBubbleStyle: React.CSSProperties = isNv
    ? {
        background: '#FFFFFF',
        borderRadius: '16px 16px 0px 16px',
        color: '#2E2E2E',
        fontFamily: "'WC-Involve', sans-serif",
        fontWeight: 400,
        fontSize: '12px',
        lineHeight: '16px',
        padding: '12px 12px 4px',
      }
    : isNevi
      ? {
          background: '#F9AA66',
          borderRadius: '12px',
          color: '#2B323B',
          fontFamily: "'WC-Satoshi', system-ui, sans-serif",
          fontWeight: 400,
          fontSize: '14px',
          lineHeight: '20px',
          padding: '8px 12px',
        }
      : {};

  const nvBotBubbleStyle: React.CSSProperties = isNv
    ? {
        background: 'transparent',
        color: '#2E2E2E',
        fontFamily: "'WC-Involve', sans-serif",
        fontWeight: 400,
        fontSize: '12px',
        lineHeight: '16px',
        padding: '8px 12px',
      }
    : isNevi
      ? {
          background: '#2B323B',
          borderRadius: '16px',
          color: '#FBFBFB',
          fontFamily: "'WC-Satoshi', system-ui, sans-serif",
          fontWeight: 400,
          fontSize: '14px',
          lineHeight: '20px',
          padding: '8px 12px',
        }
      : {};

  // NV: bot messages have no timestamp
  const showTimestamp = !(isNv && isBot);

  // Preset timestamp style
  const nvTimestampStyle: React.CSSProperties = isNv
    ? {
        fontFamily: "'WC-Involve', sans-serif",
        fontWeight: 400,
        fontSize: '8px',
        lineHeight: '10px',
        color: 'rgba(46, 46, 46, 0.5)',
      }
    : isNevi
      ? {
          fontFamily: "'WC-Satoshi', system-ui, sans-serif",
          fontWeight: 400,
          fontSize: '9px',
          lineHeight: '12px',
          color: 'rgba(251, 251, 251, 0.5)',
        }
      : {
          color: isBot ? 'var(--wc-bot-message-time-color)' : 'var(--wc-user-message-time-color)',
          opacity: 0.5,
        };

  const nvImageStyle: React.CSSProperties = isNv
    ? {
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
      }
    : isNevi
      ? {
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.25)',
        }
      : {};

  // NeVi: blob tail of two circles outside the bubble — Figma nodes 1905:18616 / 1903:19033
  const renderNeviTail = (color: string) => (
    <div
      className="wc-absolute wc-flex wc-items-end wc-pointer-events-none"
      style={{ bottom: '-2px', ...(isBot ? { left: '-8px' } : { right: '-8px' }) }}
    >
      {isBot ? (
        <>
          <div style={{ width: '6px', height: '6px', borderRadius: '16px', background: color }} />
          <div style={{ width: '16px', height: '16px', borderRadius: '16px', background: color }} />
        </>
      ) : (
        <>
          <div style={{ width: '16px', height: '16px', borderRadius: '16px', background: color }} />
          <div style={{ width: '6px', height: '6px', borderRadius: '16px', background: color }} />
        </>
      )}
    </div>
  );

  // NeVi: double-check message state — Figma node 1903:19038
  const neviChecks = (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ display: 'block' }}>
      <g transform="translate(5 4)">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M9.0308 0.168742C9.25258 -0.0562102 9.61205 -0.0562844 9.83378 0.168742C10.0554 0.393758 10.0554 0.758575 9.83378 0.983584L4.17452 6.72574C3.81389 7.09221 3.22996 7.09062 2.87024 6.72574L0.166312 3.98184C-0.0554436 3.75681 -0.0554313 3.39204 0.166312 3.167C0.388069 2.94196 0.747525 2.94197 0.969285 3.167L3.52238 5.75784L9.0308 0.168742Z"
          fill="#2B323B"
        />
      </g>
      <g transform="translate(1 4)" opacity="0.4">
        <path d="M9.00118 0.168749C9.22968 -0.0562657 9.6001 -0.0562336 9.82861 0.168749C10.0571 0.393784 10.0571 0.758548 9.82861 0.98358L7.39278 3.3823C7.16426 3.60734 6.79386 3.60734 6.56534 3.3823C6.33686 3.15727 6.33684 2.79249 6.56534 2.56747L9.00118 0.168749Z" fill="#2B323B" />
        <path d="M0.171386 3.46708C0.399901 3.24205 0.770308 3.24205 0.998822 3.46708L3.32494 5.75776L4.73829 4.3667C4.9668 4.14174 5.33723 4.1417 5.56572 4.3667C5.79422 4.59171 5.79417 4.95649 5.56572 5.18153L3.99771 6.72566C3.62616 7.09145 3.02449 7.09145 2.65293 6.72566L0.171386 4.28191C-0.0571284 4.05688 -0.0571287 3.69212 0.171386 3.46708Z" fill="#2B323B" />
      </g>
    </svg>
  );

  // User bubble classes for classic preset
  const classicUserBubbleClass = 'wc-text-[var(--wc-user-message-text-color)] wc-rounded-br-sm';
  const classicUserBubbleStyle: React.CSSProperties = {
    background:
      'linear-gradient(135deg, var(--wc-user-message-gradient-start) 0%, var(--wc-user-message-gradient-end) 100%)',
  };

  // Bot bubble classes for classic preset
  const classicBotBubbleClass =
    'wc-bg-[var(--wc-bot-message-color)] wc-text-[var(--wc-bot-message-text-color)] wc-rounded-bl-sm';

  // NeVi: any message with images renders inside a bubble — Figma node 1911:19960
  if (isNevi && hasImages) {
    const bubbleColor = isBot ? '#2B323B' : '#F9AA66';
    const bubbleStyle: React.CSSProperties = {
      padding: '4px',
      maxWidth: '80%',
      /* альбому нужна явная ширина: его содержимое absolute и не даёт
         баблу intrinsic-размера; 8 = горизонтальный padding бабла */
      ...(isMultipleImages
        ? { width: `min(${(albumLayout?.containerWidth ?? MAX_ALBUM_WIDTH) + 8}px, 80%)` }
        : {}),
    };
    /* Фон бабла — отдельным слоем ПОВЕРХ хвостика: внутренняя половина
       кружков хвоста прячется под баблом и не рисуется поверх картинки,
       снаружи остаётся обычный blob как у текстовых пузырей */
    const bubbleBg = (
      <div
        className="wc-absolute wc-inset-0 wc-pointer-events-none"
        style={{ background: bubbleColor, borderRadius: isBot ? '16px' : '12px' }}
      />
    );
    const renderImagesInBubble = () => {
      if (isMultipleImages) {
        if (!albumLayout) {
          return (
            <div className="wc-grid wc-grid-cols-2 wc-gap-0.5" style={{ width: '100%', maxWidth: MAX_ALBUM_WIDTH }}>
              {resolvedImages!.map((imageUrl, index) => (
                <img
                  key={index}
                  src={imageUrl}
                  alt={`Image ${index + 1}`}
                  className="wc-w-full wc-h-24 wc-object-cover wc-cursor-pointer hover:wc-opacity-90 wc-transition-opacity"
                  style={{ borderRadius: '10px' }}
                  onClick={() => handleImageClick(index)}
                  loading="lazy"
                />
              ))}
            </div>
          );
        }
        /* Раскладка посчитана в px для MAX_ALBUM_WIDTH; рендерим в процентах,
           чтобы альбом ужимался под фактическую ширину бабла (maxWidth 80%)
           и фото не вылезали за рамку */
        const { containerWidth: albW, containerHeight: albH } = albumLayout;
        return (
          <div
            className="wc-relative wc-overflow-hidden"
            style={{
              width: '100%',
              maxWidth: albW,
              aspectRatio: `${albW} / ${albH}`,
              borderRadius: '10px',
            }}
          >
            {resolvedImages!.map((imageUrl, index) => {
              const { dimensions } = albumLayout.layout[index];
              return (
                <img
                  key={index}
                  src={imageUrl}
                  alt={`Image ${index + 1}`}
                  className="wc-absolute wc-object-cover wc-cursor-pointer hover:wc-opacity-90 wc-transition-opacity"
                  style={{
                    left: `${(dimensions.x / albW) * 100}%`,
                    top: `${(dimensions.y / albH) * 100}%`,
                    width: `${(dimensions.width / albW) * 100}%`,
                    height: `${(dimensions.height / albH) * 100}%`,
                  }}
                  onClick={() => handleImageClick(index)}
                  loading="lazy"
                />
              );
            })}
          </div>
        );
      }
      return (
        <img
          src={resolvedImages![0]}
          alt="Image"
          className="wc-max-w-full wc-h-auto wc-cursor-pointer hover:wc-opacity-90 wc-transition-opacity"
          style={{ borderRadius: '10px', display: 'block' }}
          onClick={() => handleImageClick(0)}
          loading="lazy"
        />
      );
    };
    return (
      <div
        className={`wc-flex wc-items-center wc-gap-2 ${isBot ? 'wc-justify-start' : 'wc-justify-end'} wc-mb-3 ${animationClass}`}
      >
        {!isBot && (
          <span className="wc-whitespace-nowrap wc-flex-shrink-0" style={nvTimestampStyle}>
            {timestamp}
          </span>
        )}
        <div className="wc-relative" style={bubbleStyle}>
          {renderNeviTail(bubbleColor)}
          {bubbleBg}
          <div className="wc-relative">
          {renderImagesInBubble()}
          {(hasText || hasFiles) && (
            <div className="wc-flex wc-items-end wc-gap-2" style={{ padding: '4px 8px 6px' }}>
              {hasText && (
                <span
                  className="wc-whitespace-pre-wrap wc-break-words wc-message-content wc-min-w-0"
                  style={{
                    color: isBot ? '#FBFBFB' : '#2B323B',
                    fontFamily: "'WC-Satoshi', system-ui, sans-serif",
                    fontSize: '14px',
                    lineHeight: '20px',
                  }}
                >
                  {parse(sanitizeHtml(parseMarkdown(balanceHtmlTags(message.content))))}
                  {renderFiles()}
                </span>
              )}
              {!hasText && renderFiles()}
              {!isBot && <span className="wc-flex-shrink-0 wc-pb-[2px]">{neviChecks}</span>}
            </div>
          )}
          </div>
        </div>
        {isBot && (
          <span className="wc-whitespace-nowrap wc-flex-shrink-0" style={nvTimestampStyle}>
            {timestamp}
          </span>
        )}

        {lightboxOpen && (
          <ImageLightbox images={resolvedImages!} initialIndex={lightboxIndex} onClose={() => setLightboxOpen(false)} />
        )}
      </div>
    );
  }

  // Single image without text - no bubble, time on image
  if (isSingleImageNoText) {
    return (
      <div
        className={`wc-flex ${isBot ? 'wc-justify-start' : 'wc-justify-end'} ${isNv ? 'wc-mb-1' : 'wc-mb-3'} ${animationClass}`}
      >
        <div className="wc-max-w-[80%] wc-relative">
          <img
            src={resolvedImages![0]}
            alt="Image"
            className={`wc-max-w-full wc-h-auto wc-cursor-pointer hover:wc-opacity-90 wc-transition-opacity ${
              isPresetStyled ? '' : `wc-rounded-xl ${isBot ? 'wc-rounded-bl-sm' : 'wc-rounded-br-sm'}`
            }`}
            style={isPresetStyled ? nvImageStyle : undefined}
            onClick={() => handleImageClick(0)}
            loading="lazy"
          />
          <span className="wc-absolute wc-bottom-2 wc-right-2 wc-text-[10px] wc-text-white wc-bg-black/50 wc-px-1.5 wc-py-0.5 wc-rounded">
            {timestamp}
          </span>
        </div>

        {lightboxOpen && (
          <ImageLightbox images={resolvedImages!} initialIndex={lightboxIndex} onClose={() => setLightboxOpen(false)} />
        )}
      </div>
    );
  }

  // Multiple images - album layout
  if (isMultipleImages) {
    const renderAlbum = () => {
      // Default grid while loading
      if (!albumLayout) {
        return (
          <div className="wc-grid wc-grid-cols-2 wc-gap-0.5" style={{ width: MAX_ALBUM_WIDTH }}>
            {resolvedImages!.map((imageUrl, index) => (
              <img
                key={index}
                src={imageUrl}
                alt={`Image ${index + 1}`}
                className="wc-w-full wc-h-24 wc-object-cover wc-cursor-pointer hover:wc-opacity-90 wc-transition-opacity"
                onClick={() => handleImageClick(index)}
                loading="lazy"
              />
            ))}
          </div>
        );
      }

      // Telegram-style layout
      return (
        <div
          className="wc-relative"
          style={{
            width: albumLayout.containerWidth,
            height: albumLayout.containerHeight,
          }}
        >
          {resolvedImages!.map((imageUrl, index) => {
            const { dimensions } = albumLayout.layout[index];
            return (
              <img
                key={index}
                src={imageUrl}
                alt={`Image ${index + 1}`}
                className="wc-absolute wc-object-cover wc-cursor-pointer hover:wc-opacity-90 wc-transition-opacity"
                style={{
                  left: dimensions.x,
                  top: dimensions.y,
                  width: dimensions.width,
                  height: dimensions.height,
                }}
                onClick={() => handleImageClick(index)}
                loading="lazy"
              />
            );
          })}
        </div>
      );
    };

    const albumRoundingClass = isPresetStyled
      ? ''
      : isBot
        ? hasText
          ? 'wc-rounded-t-xl wc-rounded-tl-sm'
          : 'wc-rounded-xl wc-rounded-bl-sm'
        : hasText
          ? 'wc-rounded-t-xl wc-rounded-tr-sm'
          : 'wc-rounded-xl wc-rounded-br-sm';

    return (
      <div
        className={`wc-flex ${isBot ? 'wc-justify-start' : 'wc-justify-end'} ${isNv ? 'wc-mb-1' : 'wc-mb-3'} ${animationClass}`}
      >
        <div className="wc-max-w-[80%] wc-flex wc-flex-col">
          {/* Album */}
          <div
            className={`wc-relative wc-overflow-hidden ${albumRoundingClass}`}
            style={isPresetStyled ? nvImageStyle : undefined}
          >
            {renderAlbum()}
            {/* Time on last image if no text */}
            {!hasText && (
              <span className="wc-absolute wc-bottom-2 wc-right-2 wc-text-[10px] wc-text-white wc-bg-black/50 wc-px-1.5 wc-py-0.5 wc-rounded wc-z-10">
                {timestamp}
              </span>
            )}
          </div>

          {/* Text bubble below if has text */}
          {hasText && (
            <div
              className={`wc-px-3 wc-py-2 wc-relative ${
                isPresetStyled
                  ? ''
                  : isBot
                    ? `${classicBotBubbleClass} wc-rounded-b-xl`
                    : `${classicUserBubbleClass} wc-rounded-b-xl`
              }`}
              style={{
                width: albumLayout?.containerWidth || MAX_ALBUM_WIDTH,
                ...(isPresetStyled
                  ? isBot
                    ? nvBotBubbleStyle
                    : nvUserBubbleStyle
                  : !isBot
                    ? classicUserBubbleStyle
                    : undefined),
              }}
            >
              <span className="wc-text-sm wc-whitespace-pre-wrap wc-break-words wc-message-content wc-leading-relaxed">
                {parse(sanitizeHtml(parseMarkdown(balanceHtmlTags(message.content))))}
                <span className="wc-inline-block wc-w-[45px] wc-h-[1em] wc-align-bottom" aria-hidden="true"></span>
              </span>
              {renderFiles()}
              <span
                className="wc-absolute wc-bottom-[6px] wc-right-[10px] wc-text-[9px] wc-whitespace-nowrap wc-pointer-events-none"
                style={{
                  color: isBot ? 'var(--wc-bot-message-time-color)' : 'var(--wc-user-message-time-color)',
                  opacity: 0.5,
                }}
              >
                {timestamp}
              </span>
            </div>
          )}
        </div>

        {lightboxOpen && (
          <ImageLightbox images={resolvedImages!} initialIndex={lightboxIndex} onClose={() => setLightboxOpen(false)} />
        )}
      </div>
    );
  }

  // Single image with text - image on top, text bubble below
  if (hasImages && hasText) {
    return (
      <div
        className={`wc-flex ${isBot ? 'wc-justify-start' : 'wc-justify-end'} ${isNv ? 'wc-mb-1' : 'wc-mb-3'} ${animationClass}`}
      >
        <div className="wc-max-w-[80%] wc-flex wc-flex-col">
          {/* Image without bubble */}
          <img
            src={resolvedImages![0]}
            alt="Image"
            className={`wc-max-w-full wc-h-auto wc-cursor-pointer hover:wc-opacity-90 wc-transition-opacity ${
              isPresetStyled ? '' : isBot ? 'wc-rounded-t-xl wc-rounded-tl-sm' : 'wc-rounded-t-xl wc-rounded-tr-sm'
            }`}
            style={isPresetStyled ? nvImageStyle : undefined}
            onClick={() => handleImageClick(0)}
            loading="lazy"
          />

          {/* Text bubble below */}
          <div
            className={`wc-px-3 wc-py-2 wc-relative ${
              isPresetStyled
                ? ''
                : isBot
                  ? `${classicBotBubbleClass} wc-rounded-b-xl`
                  : 'wc-text-white wc-rounded-b-xl wc-rounded-br-sm'
            }`}
            style={isPresetStyled ? (isBot ? nvBotBubbleStyle : nvUserBubbleStyle) : !isBot ? classicUserBubbleStyle : undefined}
          >
            <span className="wc-text-sm wc-whitespace-pre-wrap wc-break-words wc-message-content wc-leading-relaxed">
              {parse(sanitizeHtml(parseMarkdown(balanceHtmlTags(message.content))))}
              <span className="wc-inline-block wc-w-[45px] wc-h-[1em] wc-align-bottom" aria-hidden="true"></span>
            </span>
            {renderFiles()}
            <span
              className="wc-absolute wc-bottom-[6px] wc-right-[10px] wc-text-[9px] wc-whitespace-nowrap wc-pointer-events-none"
              style={{
                color: isBot ? 'var(--wc-bot-message-time-color)' : 'var(--wc-user-message-time-color)',
                opacity: 0.5,
              }}
            >
              {timestamp}
            </span>
          </div>
        </div>

        {lightboxOpen && (
          <ImageLightbox images={resolvedImages!} initialIndex={lightboxIndex} onClose={() => setLightboxOpen(false)} />
        )}
      </div>
    );
  }

  // Text only - standard bubble with Telegram-style time positioning
  if (isNevi) {
    // NeVi: timestamp outside the bubble, blob tail, double check on user messages — Figma node 1905:18611
    const bubbleColor = isBot ? '#2B323B' : '#F9AA66';
    return (
      <div
        className={`wc-flex wc-items-center wc-gap-2 ${isBot ? 'wc-justify-start' : 'wc-justify-end'} wc-mb-3 ${animationClass}`}
      >
        {!isBot && (
          <span className="wc-whitespace-nowrap wc-flex-shrink-0" style={nvTimestampStyle}>
            {timestamp}
          </span>
        )}
        <div
          className="wc-relative wc-max-w-[75%] wc-flex wc-items-end wc-gap-2"
          style={isBot ? nvBotBubbleStyle : nvUserBubbleStyle}
        >
          <span className="wc-text-sm wc-whitespace-pre-wrap wc-break-words wc-message-content wc-leading-relaxed wc-min-w-0">
            {parse(sanitizeHtml(parseMarkdown(balanceHtmlTags(message.content))))}
            {renderFiles()}
          </span>
          {!isBot && <span className="wc-flex-shrink-0 wc-pb-[2px]">{neviChecks}</span>}
          {renderNeviTail(bubbleColor)}
        </div>
        {isBot && (
          <span className="wc-whitespace-nowrap wc-flex-shrink-0" style={nvTimestampStyle}>
            {timestamp}
          </span>
        )}

        {lightboxOpen && hasImages && (
          <ImageLightbox images={resolvedImages!} initialIndex={lightboxIndex} onClose={() => setLightboxOpen(false)} />
        )}
      </div>
    );
  }

  if (isPresetStyled) {
    return (
      <div
        className={`wc-flex ${isBot ? 'wc-justify-start' : 'wc-justify-end'} ${isNv ? 'wc-mb-1' : 'wc-mb-3'} ${animationClass}`}
      >
        <div
          className={`wc-px-3 wc-py-2 wc-relative wc-w-fit wc-max-w-[80%] ${isBot ? '' : 'wc-rounded-xl'}`}
          style={isBot ? nvBotBubbleStyle : nvUserBubbleStyle}
        >
          <span className="wc-text-sm wc-whitespace-pre-wrap wc-break-words wc-message-content wc-leading-relaxed">
            {parse(sanitizeHtml(parseMarkdown(balanceHtmlTags(message.content))))}
            {showTimestamp && (
              <span className="wc-inline-block wc-w-[45px] wc-h-[1em] wc-align-bottom" aria-hidden="true"></span>
            )}
          </span>
          {renderFiles()}
          {showTimestamp && (
            <span
              className="wc-absolute wc-bottom-[6px] wc-right-[10px] wc-text-[9px] wc-whitespace-nowrap wc-pointer-events-none"
              style={nvTimestampStyle}
            >
              {timestamp}
            </span>
          )}
        </div>

        {lightboxOpen && hasImages && (
          <ImageLightbox images={resolvedImages!} initialIndex={lightboxIndex} onClose={() => setLightboxOpen(false)} />
        )}
      </div>
    );
  }

  // Classic text-only bubble
  return (
    <div
      className={`wc-flex ${isBot ? 'wc-justify-start' : 'wc-justify-end'} ${isNv ? 'wc-mb-1' : 'wc-mb-3'} ${animationClass}`}
    >
      <div
        className={`wc-rounded-xl wc-px-3 wc-py-2 wc-relative wc-w-fit wc-max-w-[80%] ${
          isBot ? classicBotBubbleClass : classicUserBubbleClass
        }`}
        style={!isBot ? classicUserBubbleStyle : undefined}
      >
        <span className="wc-text-sm wc-whitespace-pre-wrap wc-break-words wc-message-content wc-leading-relaxed">
          {parse(sanitizeHtml(parseMarkdown(balanceHtmlTags(message.content))))}
          {/* Invisible spacer to reserve space for time on the same line if it fits */}
          <span className="wc-inline-block wc-w-[45px] wc-h-[1em] wc-align-bottom" aria-hidden="true"></span>
        </span>
        {renderFiles()}
        {/* Absolutely positioned time that overlaps the spacer */}
        <span
          className="wc-absolute wc-bottom-[6px] wc-right-[10px] wc-text-[9px] wc-whitespace-nowrap wc-pointer-events-none"
          style={{
            color: isBot ? 'var(--wc-bot-message-time-color)' : 'var(--wc-user-message-time-color)',
            opacity: 0.5,
          }}
        >
          {timestamp}
        </span>
      </div>

      {lightboxOpen && hasImages && (
        <ImageLightbox images={resolvedImages!} initialIndex={lightboxIndex} onClose={() => setLightboxOpen(false)} />
      )}
    </div>
  );
});

Message.displayName = 'Message';
